import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useCallback } from 'react';

/**
 * Custom hook for making authenticated API calls
 * Handles token validation and refresh automatically
 */
export const useAuthenticatedApi = () => {
  const { getValidToken, logout } = useAuth();

  const makeAuthenticatedCall = useCallback(async <T>(
    functionName: string,
    body: any = {},
    options: { retryOnAuth?: boolean } = { retryOnAuth: true }
  ): Promise<{ data?: T; error?: string }> => {
    try {
      // Get a valid token (will refresh if needed)
      const token = await getValidToken();
      
      if (!token) {
        return { error: 'Authentication required' };
      }

      // Make the API call with the token
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { ...body, token }
      });

      // Handle authentication errors
      if (error) {
        const errorMessage = error.message || 'Unknown error';
        
        // Check if it's an authentication error
        if (error.message?.includes('authentication') || 
            error.message?.includes('unauthorized') ||
            error.message?.includes('token')) {
          
          if (options.retryOnAuth) {
            // Try to refresh token and retry once
            console.log('Authentication error, attempting token refresh...');
            const newToken = await getValidToken();
            
            if (newToken) {
              // Retry the call with refreshed token
              return makeAuthenticatedCall(functionName, body, { retryOnAuth: false });
            }
          }
          
          // If refresh failed or we've already retried, logout
          logout();
          return { error: 'Session expired. Please log in again.' };
        }

        return { error: errorMessage };
      }

      // Handle API-level errors
      if (data?.error) {
        const apiError = data.error;
        
        // Check if API returned an authentication error
        if (typeof apiError === 'string' && 
            (apiError.includes('authentication') || 
             apiError.includes('unauthorized') ||
             apiError.includes('token'))) {
          logout();
          return { error: 'Session expired. Please log in again.' };
        }
        
        return { error: apiError };
      }

      return { data };
    } catch (error) {
      console.error(`Error in ${functionName}:`, error);
      return { 
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }, [getValidToken, logout]);

  return { makeAuthenticatedCall };
};